import { Chess } from 'chess.js';
import { START_FEN, type Move, type Square } from './types';

export function turnNumber(startFen: string, plyIndex: number): number {
  const parts = startFen.trim().split(/\s+/);
  const blackFirst = parts[1] === 'b';
  const full = Number(parts[5] ?? '1') || 1;
  return full + Math.floor((plyIndex + (blackFirst ? 1 : 0)) / 2);
}

export function castleRook(
  from: Square,
  to: Square,
): { from: Square; to: Square } | null {
  if (from[1] !== to[1]) return null;
  const rank = from[1]!;
  if (from[0] === 'e' && to[0] === 'g') {
    return { from: `h${rank}` as Square, to: `f${rank}` as Square };
  }
  if (from[0] === 'e' && to[0] === 'c') {
    return { from: `a${rank}` as Square, to: `d${rank}` as Square };
  }
  return null;
}

/** Newest turn + up to this many older (scrolled) turns. */
export const MAX_SCROLLED_TURNS = 3;

function pushTurn(stack: number[] | undefined, turn: number): number[] {
  return [turn, ...(stack ?? [])].slice(0, 1 + MAX_SCROLLED_TURNS);
}

function takeStack(labels: Map<Square, number[]>, sq: Square): number[] {
  const stack = labels.get(sq);
  labels.delete(sq);
  return stack ?? [];
}

export function applyMoveLabel(
  labels: Map<Square, number[]>,
  m: { from: string; to: string; piece: string; flags: string },
  turn: number,
): void {
  const from = m.from as Square;
  const to = m.to as Square;
  const carried = takeStack(labels, from);
  if (m.flags.includes('e')) {
    labels.delete(`${m.to[0]}${m.from[1]}` as Square);
  }
  labels.set(to, pushTurn(carried, turn));
  if (m.piece === 'k' && (m.flags.includes('k') || m.flags.includes('q'))) {
    const rook = castleRook(from, to);
    if (rook) {
      const rookStack = takeStack(labels, rook.from);
      labels.set(rook.to, pushTurn(rookStack, turn));
    }
  }
}

/** Replay a SAN line from a FEN into store-shaped moves. Null if any SAN fails. */
export function historyFromSans(
  moves: string,
  startFen: string = START_FEN,
): Move[] | null {
  const game = new Chess(startFen);
  const history: Move[] = [];
  const sans = moves.trim().split(/\s+/).filter(Boolean);
  for (const san of sans) {
    let m;
    try {
      m = game.move(san);
    } catch {
      return null;
    }
    if (!m) return null;
    history.push({
      from: m.from as Square,
      to: m.to as Square,
      promotion: m.promotion as Move['promotion'],
      san: m.san,
      uci: m.lan ?? `${m.from}${m.to}${m.promotion ?? ''}`,
    });
  }
  return history;
}

/** Turn stacks (newest first) for each square whose piece has moved. */
export function lastMoveTurnsFromSans(
  moves: string,
  startFen: string = START_FEN,
): Map<Square, number[]> {
  const history = historyFromSans(moves, startFen);
  if (!history) return new Map();
  return lastMoveTurnsFromHistory(startFen, history);
}

export function lastMoveTurnsFromHistory(
  startFen: string,
  history: Array<{ from: Square; to: Square; promotion?: string }>,
): Map<Square, number[]> {
  const game = new Chess(startFen);
  const labels = new Map<Square, number[]>();
  history.forEach((mv, i) => {
    const m = game.move({
      from: mv.from,
      to: mv.to,
      promotion: mv.promotion,
    });
    if (!m) return;
    applyMoveLabel(labels, m, turnNumber(startFen, i));
  });
  return labels;
}
